import jwtDecode from "jwt-decode";

const MIDWAY_URL = "https://midway-auth.amazon.com/SSO";
const TIMEOUT_BUFFER_SECONDS = 30;
const RETRY_ATTEMPTS = 3;
const ID_TOKEN = "id_token";
const STATE = "state";

const token = { value: "" };
let inFlightRefresh: Promise<void> | undefined;
let refreshTimer: number | undefined;
const midwayParams = {
  scope: "openid",
  response_type: ID_TOKEN,
  client_id: encodeURIComponent(`${window.location.host}`),
  redirect_uri: "",
  nonce: "",
};

/**
 * Returns a Midway identity token that is valid for at least TIMEOUT_BUFFER_SECONDS.
 *
 * The token identifies the user to the website (alias, RUM); API requests authenticate with
 * MidwayAuthorizer's session cookie instead. The expiry is checked on every call rather than
 * trusting the refresh timer, which a sleeping tab can miss.
 */
export async function getMidwayJwtToken(): Promise<string> {
  if (!token.value || isExpiringSoon(token.value)) {
    await refreshToken();
  }

  return token.value;
}

export function getCurrentMidwaySubject(): string | undefined {
  if (!token.value) {
    return undefined;
  }
  const subject = (jwtDecode(token.value) as { sub?: unknown }).sub;
  return typeof subject === "string" && subject.trim() ? subject : undefined;
}

function isExpiringSoon(jwt: string): boolean {
  try {
    const { exp } = jwtDecode(jwt) as { exp?: number };
    if (typeof exp !== "number") {
      return true;
    }
    return exp - Math.round(Date.now() / 1000) <= TIMEOUT_BUFFER_SECONDS;
  } catch {
    return true;
  }
}

/** Single in-flight refresh so concurrent API calls do not each hit Midway. */
function refreshToken(): Promise<void> {
  if (!inFlightRefresh) {
    inFlightRefresh = doRefreshToken().finally(() => {
      inFlightRefresh = undefined;
    });
  }
  return inFlightRefresh;
}

async function doRefreshToken() {
  const returned = new URLSearchParams(window.location.search).get(ID_TOKEN);
  let fresh: string;
  if (returned) {
    removeMidwaySearchParams();
    fresh = returned;
  } else {
    updateMidwayParams();
    const queryParams = Object.keys(midwayParams)
      .map((key) => `${key}=${midwayParams[key]}`)
      .join("&");
    const url = `${MIDWAY_URL}?${queryParams}`;
    fresh = await fetch(url, { credentials: "include" }).then((response) => {
      if (response.status === 401) {
        // Redirect the user to Midway login
        window.location.replace(`${MIDWAY_URL}/redirect?${queryParams}`);
      }
      removeMidwaySearchParams();
      return response.text();
    });
  }
  // Only replace the cached token once the new one decodes; a failed fetch
  // leaves the old value in place and the next call will try again.
  const decodedToken: any = jwtDecode(fresh);
  token.value = fresh;
  const expiration = decodedToken.exp;
  if (refreshTimer !== undefined) {
    window.clearTimeout(refreshTimer);
  }
  refreshTimer = window.setTimeout(
    () => retryWithJitter(refreshToken),
    Math.max(
      0,
      (expiration - Math.round(Date.now() / 1000) - TIMEOUT_BUFFER_SECONDS) *
        1000
    )
  );
}

export function getUserAlias(): string {
  const decoded: any = jwtDecode(token.value);
  return decoded.sub ?? "";
}

/**
 * Calls an asynchronous function with retries and jitter built in using timeouts to simulate recursion
 * @param func Function reference to the function that should be executed
 * @param attempt The attempt number
 * @param error The last error that was received
 * @returns {Promise<void>}
 * @private
 */
async function retryWithJitter(
  func: Function,
  attempt: number = 0,
  error: any | unknown = null
) {
  if (attempt === RETRY_ATTEMPTS - 1) {
    // Give up on the background refresh; getMidwayJwtToken() refreshes on
    // demand once the token is expired, so nothing is stuck.
    console.warn("Midway background token refresh failed", error);
    return;
  }
  try {
    await func();
  } catch (e: any | unknown) {
    window.setTimeout(() => retryWithJitter(func, attempt + 1, e), 500);
  }
}

function randomString() {
  return Math.random().toString(36).substring(2);
}

function updateMidwayParams() {
  midwayParams.nonce = randomString() + randomString();
  midwayParams.redirect_uri = encodeURIComponent(`${window.location}`);
}

/**
 * Removes from current location the URL params retuned by Midway (e.g. 'id_token',
 * 'state') after login redirection and updates browser history so they do not
 * appear in the current URL
 */
function removeMidwaySearchParams() {
  const newLocation = removeLocationSearchParameters(ID_TOKEN, STATE);
  window.history.replaceState({}, "", newLocation);
}

/**
 * Removes the passed keys from the current location search (query)
 * @param keys
 * @returns a new location without the keys
 */
function removeLocationSearchParameters(...keys: string[]): string {
  const location = window.location;
  const queryParams = new URLSearchParams(location.search);
  keys.forEach((key) => queryParams.delete(key));
  const queryString = queryParams.toString();
  const base = location.href.replace(location.search, "");
  const newUrl = `${base}${queryString === "" ? "" : "?"}${queryString}`;
  return newUrl;
}
